import CancelIcon from '@mui/icons-material/Cancel';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { makeStyles } from 'tss-react/mui';

import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { authSelectors } from 'app-components/flux/auth';
import { logoStorageSlice } from 'app-components/flux/storage/logoStorageSlice';
import QButton from 'components/QButton';
import { useAppDispatch, useAppStore } from 'hooks/redux';
import { useEffectOnce } from 'react-use';

// expected & ideal image size for logo
const IMG_WIDTH = 225;
const IMG_HEIGHT = 100;

const useStyles = makeStyles()((theme) => ({
  startAdornment: {
    position: 'relative',
    flexDirection: 'row',
    flexShrink: 0,
    minWidth: IMG_WIDTH,
    minHeight: IMG_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: theme.palette.divider,
    margin: `${theme.spacing(4)} ${theme.spacing(0)}`,
  },
  logo: {
    width: IMG_WIDTH,
    height: IMG_HEIGHT,
    objectFit: 'cover',
    flexShrink: 0,
    borderRadius: 0,
  },
  uploadIcon: {
    marginBottom: 2,
    marginRight: 4,
  },
  closeIcon: {
    fontSize: '20px',
    color: theme.palette.border.dark,
  },
}));

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_FORMATS = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/heic'];

const BusinessLogoField = ({
  initialLogoId,
  onSelect,
}: {
  initialLogoId?: string;
  onSelect: (docId: string) => void;
}) => {
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState<string | undefined>(undefined);

  const syncUrl = useCallback(
    async (logoId: string) => {
      try {
        // Get cdnUrl (sync if needed)
        let cdnUrl = logoStorageSlice.selectors.selectById(store.getState(), logoId)?.url;
        if (!cdnUrl) {
          await dispatch(logoStorageSlice.actions.fetch()).unwrap();
          cdnUrl = (await dispatch(logoStorageSlice.actions.syncNoCdnUrl(logoId)).unwrap())?.url;
        }

        // Grab token & dataset
        const { accessToken, datasetId } = authSelectors.getAuthSession(store.getState());
        if (!(cdnUrl && accessToken && datasetId)) {
          throw new Error('Cannot fetch image');
        }

        // Fetch image
        const response = await fetch(cdnUrl, {
          method: 'GET',
          // Creds not needed for cdn false.
          // credentials: 'include',
          // headers: {
          //   Authorization: `Bearer ${accessToken}`,
          //   'qcs-dataset-id': datasetId,
          // },
        });
        if (!response.ok) throw new Error('Failed to fetch image');

        // Create object url
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        setSource(objectUrl);
      } catch (err) {
        verify(false, err);
        setError('Unknown error occurred while retrieving your logo.');
      }
    },
    [dispatch, store],
  );

  useEffectOnce(() => {
    if (initialLogoId) {
      (async () => {
        setLoading(true);
        await syncUrl(initialLogoId);
        setLoading(false);
      })();
    }
  });

  const onDrop = useCallback(
    async (acceptedFiles: any[], rejectedFiles: any[]) => {
      setLoading(true);
      setError(undefined);
      // Don't upload if asset is invalid.
      if (rejectedFiles.length > 0) {
        setError('Upload failed. Please use an image file (SVG or PNG preferred) that is less than 2MB.');
      } else {
        try {
          // Take accepted asset and upload it.
          const asset = acceptedFiles[0];
          const fileUri = URL.createObjectURL(asset);

          const document = await dispatch(
            logoStorageSlice.actions.uploadNewDocument({
              name: asset.name,
              mimeType: asset.type,
              fileUri,
            }),
          ).unwrap();

          URL.revokeObjectURL(asset);

          // Sync the document id with its cdn url
          if (!document.id) {
            throw 'No id on document';
          }
          onSelect(document.id);
          await new Promise(process.nextTick);
          await syncUrl(document.id);
        } catch (e) {
          assert(false, e);
          setError('Upload failed. Please try again later.');
        }
      }
      setLoading(false);
    },
    [dispatch, onSelect, syncUrl],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ALLOWED_FORMATS.join(', '),
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    noClick: true,
  });

  useEffect(() => {
    // Store source for clean up (if new source or if unmount)
    const sourceRef = source;
    return () => {
      if (sourceRef) {
        URL.revokeObjectURL(sourceRef);
      }
    };
  }, [source]);

  return (
    <Stack flexDirection={'row'} alignItems={'center'}>
      <TextField
        {...getRootProps()}
        color={'primary'}
        sx={{ opacity: isDragActive ? 0.5 : 1, flexGrow: 1 }}
        label='Upload logo'
        variant='filled'
        margin='normal'
        error={!!error}
        helperText={error ?? ''}
        slotProps={{
          input: {
            inputProps: {
              readOnly: true,
            },
            startAdornment:
              source || loading ? (
                <Stack className={classes.startAdornment}>
                  {loading ? (
                    <CircularProgress size={32} />
                  ) : (
                    <>
                      <img src={source} alt='Business Logo' className={classes.logo} />
                      <IconButton
                        onClick={() => {
                          setSource(undefined);
                          onSelect('');
                        }}
                        sx={{ flexShrink: 0 }}
                      >
                        <CancelIcon className={classes.closeIcon} />
                      </IconButton>
                    </>
                  )}
                </Stack>
              ) : null,
            endAdornment: (
              <>
                <QButton id='attach-button' onClick={open} aria-label='add attachment' sx={{ flexShrink: 0 }}>
                  <CloudUploadIcon className={classes.uploadIcon} /> UPLOAD
                </QButton>
                {/** Input used for validation */}
                <input {...getInputProps()} style={{ display: 'none' }} />
              </>
            ),
          },
        }}
      />
    </Stack>
  );
};

export default React.memo(BusinessLogoField);
